import { CHARACTER_SET } from 'libs/constants';

// Arrays in JavaScript are typed as objects, so this util works for comparing
// both hash maps `{}` and arrays `[]`
export const deepEqual = (object1: object, object2: object) => {
    if (object1 === object2) {
        return true;
    }
    if (
        typeof object1 !== 'object' ||
        typeof object2 !== 'object' ||
        object1 == null ||
        object2 == null
    ) {
        return false;
    }
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);
    if (keys1.length !== keys2.length) {
        return false;
    }
    for (const key of keys1) {
        // Not sure how to handle this type error since we don't know what objects
        // will be passed to the comparison function here
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!keys2.includes(key) || !deepEqual(object1[key], object2[key])) {
            return false;
        }
    }
    return true;
};

export const generateRandomString = () => {
    let randomString = '';
    for (let i = 0; i < 10; i++) {
        randomString += CHARACTER_SET.charAt(
            Math.floor(Math.random() * CHARACTER_SET.length)
        );
    }
    return randomString;
};

export const toDate = (raw: unknown): Date | null => {
    if (raw == null) return null;

    if (raw instanceof Date) {
        return isNaN(raw.getTime()) ? null : raw;
    }

    if (typeof raw === 'number') {
        const ms = raw < 1e12 ? raw * 1000 : raw; // seconds vs ms
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
    }

    if (typeof raw === 'string') {
        const s = raw.trim();
        if (!s) return null;

        // Pure digits? Treat as epoch seconds/ms.
        if (/^\d+$/.test(s)) {
            const n = Number(s);
            const ms = n < 1e12 ? n * 1000 : n;
            const d = new Date(ms);
            return isNaN(d.getTime()) ? null : d;
        }

        // Normalize Postgres 'YYYY-MM-DD HH:mm:ss+00' -> 'YYYY-MM-DDTHH:mm:ss+00'
        const normalized = s.includes('T') ? s : s.replace(' ', 'T');

        // If there's a time component but *no* timezone, assume UTC ('Z')
        const hasTime = /\dT\d/.test(normalized);
        const hasTZ = /[zZ]$|[+-]\d\d:?\d\d$/.test(normalized);
        const candidate = hasTime && !hasTZ ? normalized + 'Z' : normalized;

        const d = new Date(candidate);
        return isNaN(d.getTime()) ? null : d;
    }

    return null;
};

export const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'long',
    }).format(date);
};

export const formatDateTime = (date: Date) => {
    const formattedDate = new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'medium',
    }).format(date);

    return `${formattedDate.slice(0, formattedDate.length - 6)}${formattedDate
        .slice(formattedDate.length - 2, formattedDate.length)
        .toLowerCase()}`;
};

export const formatTimeInET = (date: Date) =>
    new Intl.DateTimeFormat('en-US', {
        timeStyle: 'short',
        timeZone: 'America/New_York',
    }).format(date);

export const getDayCountBetweenDates = (date1: Date, date2: Date) => {
    const utc1 = Date.UTC(
        date1.getFullYear(),
        date1.getMonth(),
        date1.getDate()
    );
    const utc2 = Date.UTC(
        date2.getFullYear(),
        date2.getMonth(),
        date2.getDate()
    );

    return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
};

export type DateRelationship = 'previous' | 'current' | 'upcoming';

export const getRelationshipToToday = (date: Date): DateRelationship => {
    const dayCountBetweenDates = getDayCountBetweenDates(date, new Date());
    if (dayCountBetweenDates === 0) {
        return 'current';
    } else if (Math.abs(dayCountBetweenDates) === dayCountBetweenDates) {
        return 'previous';
    } else {
        return 'upcoming';
    }
};

// This function is used to map the assessment questions to database insert params
// Eg. The second of three questions in "Career Wellbeing" would be cw2
export const mapDimensionQuestion = (category: string, index: number) => {
    const prefix = category
        .split(' ')
        .map((word) => word[0]?.toLowerCase())
        .join('');
    return `${prefix}${index + 1}`;
};

export const centsToDollars = (cents: number) =>
    (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

export const getInitialsFromFullName = (name: string): string => {
    if (name == null || name.trim() === '') return '';
    const parts = name.trim().split(/\s+/);
    const [first, second] = [parts[0], parts[parts.length - 1]];
    const firstInitial = first?.[0] ?? '';
    const secondInitial = second?.[0] ?? '';
    return (firstInitial + secondInitial).toUpperCase();
};

export const formatLinkedInUrl = (linkedInRefrence: string) => {
    const rootUrl = 'https://www.linkedin.com/in/';
    const trimmed = linkedInRefrence.trim();

    if (/^https?:\/\/(www\.)?linkedin\.com\//i.test(trimmed)) {
        return trimmed;
    } else if (trimmed.startsWith('www')) {
        return `https://${trimmed}`;
    }

    return `${rootUrl}${trimmed}`;
};

export const formatInstagramProfileUrl = (instagramReference: string) => {
    const rootUrl = 'https://www.instagram.com/';
    const trimmed = instagramReference.trim();

    if (/^https?:\/\/(www\.)?instagram\.com\//i.test(trimmed)) {
        return trimmed;
    } else if (trimmed.startsWith('@')) {
        const handle = trimmed.slice(1);
        return `${rootUrl}${handle}`;
    } else if (trimmed.startsWith('www')) {
        return `https://${trimmed}`;
    }

    return `${rootUrl}${trimmed}`;
};

export function checkImageUrl(url: string): Promise<boolean> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
    });
}
